/** Abstract base class for concealment skills (Hide, Stealth) with shared LOS gating, success rolls, and sustained XP. */
import { ToggleSkillEffect } from '../ToggleSkillEffect';
import { clamp } from '../../utils/Math';
import { DebugLog } from '../../debug/DebugLog';
import { DebugConfig } from '../../debug/DebugConfig';
import type { RecordUseOptions } from '../../utils/Types';

/** Shared callback contract for all concealment skills. */
export interface ConcealmentCallbacks {
  anyEnemyHasLOS: () => boolean;
  getXp: () => number;
  getDanger: () => number;
  onXpGained: (skillId: string, options?: RecordUseOptions) => void;
}

/** Shared tuning fields present in both hide.json and stealth.json. */
export interface ConcealmentTuning {
  baseSuccessChance: number;
  maxSuccessChance: number;
  xpForMaxChance: number;
  proximityCheckDistancePx: number;
  dangerSuccessPenalty: number;
  dangerSuccessFloor: number;
  lowDangerBoostZero: number;
  lowDangerBoostOne: number;
}

/**
 * Template method base class for concealment skills.
 * Extends ToggleSkillEffect for active state and cooldown management.
 * Subclasses override hooks for cooldown calculation, activation prerequisites,
 * post-activation setup, update-time break conditions, and break behavior.
 */
export abstract class ConcealmentSkillEffect extends ToggleSkillEffect {
  /** Concealment skills are never manually toggled off — they break via game conditions only. */
  override readonly activationMode = 'instant' as const;
  protected sustainedXpTimer = 0;
  protected _proximityBreak = false;
  protected callbacks: ConcealmentCallbacks;

  constructor(callbacks: ConcealmentCallbacks) {
    super();
    this.callbacks = callbacks;
  }

  // ── Template methods (subclasses override) ──────────────────────────

  /** Return the tuning config for this concealment skill. */
  protected abstract getTuning(): ConcealmentTuning;

  /** Return the cooldown duration in ms for this activation attempt. */
  protected abstract getCooldownMs(): number;

  /** Subclass-specific activation prerequisite (beyond cooldown check). */
  protected abstract canActivateExtra(): boolean;

  /** Called after successful activation — subclass setup (e.g. step budget). */
  protected abstract onActivateSuccess(): void;

  /** Return true if the skill should break this frame (e.g. movement, hide dropped). */
  protected abstract shouldBreakOnUpdate(delta: number): boolean;

  /** Execute the break: apply cooldown if needed, invoke break callback. */
  protected abstract onBreakConceal(): void;

  // ── Shared implementations ──────────────────────────────────────────

  /** Success chance based on XP progress from base to max over xpForMaxChance. */
  get currentSuccessChance(): number {
    const config = this.getTuning();
    const xp = this.callbacks.getXp();
    return clamp(
      config.baseSuccessChance + (config.maxSuccessChance - config.baseSuccessChance) * (xp / config.xpForMaxChance),
      config.baseSuccessChance,
      config.maxSuccessChance,
    );
  }

  /** Apply danger penalty with low-danger boost: floor lerps from boostZero→boostOne for danger 0→1. */
  dangerAdjustedChance(baseChance: number): number {
    const config = this.getTuning();
    const danger = this.callbacks.getDanger();
    if (danger <= 0) return Math.max(baseChance, config.lowDangerBoostZero);
    const adjusted = Math.pow(baseChance, 1 + danger * config.dangerSuccessPenalty);
    const penalized = Math.max(config.dangerSuccessFloor, adjusted);
    // Low-danger boost: when danger < 1, a floor lerps from boostZero to boostOne
    if (danger < 1) {
      const boostFloor = config.lowDangerBoostZero + (config.lowDangerBoostOne - config.lowDangerBoostZero) * danger;
      return Math.max(penalized, boostFloor);
    }
    return penalized;
  }

  canActivate(): boolean {
    if (this.cooldownRemaining > 0) return false;
    return this.canActivateExtra();
  }

  getBlockReason(): string | null {
    if (this.cooldownRemaining > 0) return 'cooldown';
    if (!this.canActivateExtra()) return 'not ready';
    return null;
  }

  /** Attempt activation: LOS gate → success roll → XP award. */
  activate(): boolean {
    if (this._isActive) {
      DebugLog.log('skills', `${this.skillId}: already active, ignoring`);
      return true;
    }

    // LOS gate: if any enemy sees us, automatic fail (unless debug override)
    if (this.callbacks.anyEnemyHasLOS() && !DebugConfig.flags.alwaysSucceedSkills) {
      if (!DebugConfig.flags.zeroCooldowns) {
        this.cooldownRemaining = this.getCooldownMs();
      }
      this.callbacks.onXpGained(this.skillId, { failed: true });
      DebugLog.log('skills', `${this.skillId}: FAILED — enemy has LOS, cooldown ${this.cooldownRemaining}ms`);
      return false;
    }

    // Success roll (debug flag bypasses)
    const baseChance = this.currentSuccessChance;
    const chance = this.dangerAdjustedChance(baseChance);
    const roll = Math.random();
    if (DebugConfig.flags.alwaysSucceedSkills || roll < chance) {
      this._isActive = true;
      this.sustainedXpTimer = 0;
      this.onActivateSuccess();
      this.callbacks.onXpGained(this.skillId, {
        enemyHasLOS: this.callbacks.anyEnemyHasLOS(),
        dangerPresent: this.callbacks.getDanger() > 0,
      });
      DebugLog.log('skills', `${this.skillId}: SUCCESS (roll ${roll.toFixed(2)} < chance ${chance.toFixed(2)} [base ${baseChance.toFixed(2)}, danger ${this.callbacks.getDanger().toFixed(2)}], xp=${this.callbacks.getXp()})`);
      return true;
    }

    // Failed roll
    if (!DebugConfig.flags.zeroCooldowns) {
      this.cooldownRemaining = this.getCooldownMs();
    }
    this.callbacks.onXpGained(this.skillId, { failed: true });
    DebugLog.log('skills', `${this.skillId}: FAILED roll (${roll.toFixed(2)} >= chance ${chance.toFixed(2)} [base ${baseChance.toFixed(2)}, danger ${this.callbacks.getDanger().toFixed(2)}], xp=${this.callbacks.getXp()}), cooldown ${this.cooldownRemaining}ms`);
    return false;
  }

  /** Deactivate without triggering a cooldown or break callback. */
  protected onDeactivate(): void {
    this.sustainedXpTimer = 0;
  }

  /**
   * Called by Detection when an enemy within proximity range would detect the concealed player.
   * Returns true if the player stays hidden (check passed), false if detected.
   */
  proximityCheck(): boolean {
    if (DebugConfig.flags.alwaysSucceedSkills) return true;
    const chance = this.dangerAdjustedChance(this.currentSuccessChance);
    if (Math.random() < chance) return true;

    // Failed proximity check — break on next update
    this._proximityBreak = true;
    return false;
  }

  protected updateWhileActive(delta: number): void {
    // Subclass-specific break condition
    if (this.shouldBreakOnUpdate(delta)) {
      this.breakConceal();
      return;
    }

    // Proximity break (set by proximityCheck)
    if (this._proximityBreak) {
      this._proximityBreak = false;
      this.breakConceal();
      return;
    }

    // Sustained XP — subclasses implement their own model
    this.updateSustainedXp(delta);
  }

  /** Subclasses override to implement sustained XP behavior (danger-gated timer, step-xp, etc). */
  protected updateSustainedXp(_delta: number): void {
    // Default: no sustained XP
  }

  /** Force-break concealment: deactivate, delegate to subclass for cooldown and callback. */
  protected breakConceal(): void {
    if (!this._isActive) return;
    this._isActive = false;
    this.sustainedXpTimer = 0;
    this.onBreakConceal();
    DebugLog.log('skills', `${this.skillId}: BROKEN`);
  }
}
